/* eslint-disable no-restricted-imports */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  Input,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import {
  isCompleteShopDomain,
  normalizeShopDomain,
} from 'src/features/Settings/utils/shopDomain';

type ShopDomainModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConnect: (shopDomain: string) => void;
  isConnecting: boolean;
};

export const ShopDomainModal: React.FC<ShopDomainModalProps> = ({
  isOpen,
  onClose,
  onConnect,
  isConnecting,
}) => {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  const isValidDomain = isCompleteShopDomain(inputValue);

  const handleConnect = useCallback(() => {
    const shopDomain = normalizeShopDomain(inputValue);
    if (!shopDomain) {
      return;
    }
    onConnect(shopDomain);
  }, [inputValue, onConnect]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="16px" bg="surface.panel">
        <ModalCloseButton />
        <ModalHeader pb={2} pt={6}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="xl"
            color="content.title">
            Connect your Shopify store
          </Text>
        </ModalHeader>

        <ModalBody px={6} py={4}>
          <Text
            fontSize="sm"
            color="content.muted"
            fontFamily="marketingPage"
            mb={3}>
            Enter your store address to continue to Shopify.
          </Text>
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="my-store.myshopify.com"
            bg="surface.panel"
            border="1px solid"
            borderColor="surface.border"
            borderRadius="md"
            fontSize="16px"
            _placeholder={{ color: 'content.muted' }}
            _focus={{
              borderColor: 'icon.green',
              boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && isValidDomain) {
                handleConnect();
              }
            }}
            autoFocus
          />
        </ModalBody>

        <ModalFooter gap={3} px={6} pb={6}>
          <Button
            bg="surface.sidebarMuted"
            color="content.body"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="cancel-shop-domain"
            fontFamily="marketingPage"
            onClick={onClose}
            flex={1}
            _hover={{ bg: 'sidebar.itemHoverBg' }}>
            Cancel
          </Button>
          <Button
            bg="icon.green"
            color="white"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="connect-shop-domain"
            fontFamily="marketingPage"
            onClick={handleConnect}
            isLoading={isConnecting}
            isDisabled={!isValidDomain}
            flex={1}
            _hover={{ bg: 'green.600' }}>
            Connect
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
